import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import HeadingTitleItem from "@/components/HeadingTitleItem";
import InfoItem from "@/components/InfoItem";
import { Skeleton } from "@/components/ui/skeleton";

// Icons
import { Pen } from "lucide-react";

// Services
import { getSOE } from "@/services/soe";
import type { SOE } from "@/types/soe";
import { S3_BUCKET_LINK } from "@/components/constants";
import formatDate from "@/utils/formatDate";
import { formatNumber } from "@/utils/formatNumber";

export const Route = createFileRoute(
  "/_layout-private/account-management/soe/_information/joint-venture/$id"
)({
  component: JointVentureDetail,
});

function JointVentureDetail() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { company_management } = useAccessPermission();
  // Query
  const { data: soeRes, isLoading } = useQuery({
    queryKey: ["soe", id],
    queryFn: () => getSOE(id),
    enabled: !!id,
  });

  const soe = soeRes?.data as SOE;

  if (isLoading) {
    return <Skeleton className="w-full h-[80vh]" />;
  }

  return (
    <div className="space-y-[32px] overflow-hidden">
      <div className="flex justify-between">
        <div>
          <small>ຮູບໂລໂກ້</small>
          <div className="mt-[8px]">
            <Avatar className="w-[120px] h-[120px]">
              <AvatarImage src={S3_BUCKET_LINK + soe?.companyProfileUrl} />
              <AvatarFallback>{soe?.companyName?.charAt(0)}</AvatarFallback>
            </Avatar>
          </div>
        </div>
        <div>
          {company_management.isUpdate && (
            <Button
              onClick={() =>
                navigate({
                  to: "/account-management/soe/joint-venture/mutate",

                  search: {
                    childId: soe.id,
                    data: JSON.stringify(soe),
                  },
                })
              }
            >
              <Pen className="mr-2" />
              ແກ້ໄຂ
            </Button>
          )}
        </div>
      </div>

      <HeadingTitleItem
        title="ຂໍ້ມູນວິສາຫະກິດ"
        subTitle="ລາຍລະອຽດຂໍ້ມູນວິສາຫະກິດ"
      />
      <div className="space-y-[12px] w-full">
        <InfoItem label="ຊື່ລັດວິສາຫະກິດ" value={soe?.companyName} />
        {/* <InfoItem label="ຊື່ລັດວິສາຫະກິດອັງກິດ" value={soe?.companyNameEng} /> */}
        <InfoItem
          label="ປີສ້າງຕັ້ງ"
          value={formatDate({
            date: soe?.establishedDate as string,
            format: "DD/MM/YYYY",
          })}
        />
        <InfoItem label="ລັດຖືຫຸ້ນ" value={soe?.govShareholding} />
        {/* <InfoItem label="ເອກະຊົນພາຍໃນຖືຫຸ້ນ" value="0%" />
				<InfoItem label="ເອກະຊົນຕ່າງປະເທດຖືຫຸ້ນ" value="0%" /> */}
        {/* <InfoItem label="ສະພາບໍລິຫານ" value={"1"} />
				<InfoItem label="ຍິງ" value="1" />
				<InfoItem label="ຄະນະອຳນວຍການ" value="1" />
				<InfoItem label="ຍິງ" value="1" /> */}
        {/* <InfoItem label="ພະນັກງານທັງໝົດ" value={soe?.empCount} />
				<InfoItem label="ຍິງ" value={soe?.femaleEmpCount} /> */}
        {/* <InfoItem label="ການຖືບັນຊີ" value="ຂະຫຍາຍຕົວ" /> */}
      </div>

      {/* <HeadingTitleItem
				title="ທີ່ຕັ້ງຂອງສຳນັກງານໃຫ່ຍວິສາຫະກິດ"
				subTitle="ລາຍລະອຽດຂໍ້ມູນສຳນັກງານໃຫ່ຍວິສາຫະກິດ"
			/>
			<div className="space-y-[12px] w-full">
				<InfoItem label="ແຂວງ" value={soe?.province?.province} />
				<InfoItem label="ເມືອງ" value={soe?.district?.district} />
				<InfoItem label="ບ້ານ" value={soe?.village} />
				<InfoItem
					label="ຂັ້ນຮັບຜິດຊອບ"
					value={soe?.managementLevel === "MINISTRY" ? "ຂັ້ນສູນກາງ" : "ຂັ້ນທ້ອງຖິ່ນ"}
				/>
				<InfoItem
					label="ຂຶ້ນກັບກະຊວງ/ພະແນກ"
					value={
						soe?.managementLevel === "MINISTRY"
							? soe?.dependOfDepartment
							: soe?.dependOfProvince
					}
				/>
				<InfoItem
					label="ເບີໂທລະສັບ/ແຟັກ"
					value={`${soe?.phone} / ${soe?.faxNumber}`}
				/>
				<InfoItem
					label="Email/Website"
					value={`${soe?.email} / ${soe?.websiteUrl}`}
				/>
			</div> */}
      <HeadingTitleItem title="ການລົງທຶນ" subTitle="ລາຍລະອຽດຂໍ້ມູນການລົງທຶນ" />

      <div className="space-y-[12px] w-full">
        <InfoItem
          label="ທຶນຈົດທະບຽນ (ກີບ)"
          value={formatNumber(Number.parseFloat(soe?.registeredCapital || "0"))}
        />
        {/* <InfoItem label="ໃບອະນຸຍາດລົງທຶນເລກທີ" value={soe?.registrationNo} />
				<InfoItem
					label="ລົງວັນທີ"
					value={formatDate({
						date: soe?.registrationDate as string,
						format: "DD/MM/YYYY",
					})}
				/>
				<InfoItem label="ໃບທະບຽນສຳປະທານເລກທີ" value={soe?.concessionNo} /> */}

        <InfoItem label="ເລກທະບຽນວິສາຫະກິດ" value={soe?.registrationId} />
        <InfoItem label="ເລກປະຈຳຕົວຜູ້ເສຍອາກອນ" value={soe?.taxpayerId} />
        {/* <InfoItem label="ປະເພດວິສາຫະກິດ" value={soe?.competencyLevel} /> */}
      </div>
      <HeadingTitleItem
        title="ຂໍ້ມູນການອັບເດດບັນຊີ"
        subTitle="ລາຍລະອຽດຂໍ້ມູນການອັບເດດຂໍ້ມູນ"
      />

      <div className="space-y-[12px] w-full">
        <InfoItem
          label="ວັນທີສ້າງ"
          value={formatDate({ date: soe?.createdAt, format: "DD/MM/YYYY" })}
        />
        <InfoItem label="ສ້າງໂດຍ" value={soe?.createdBy} />
        <InfoItem
          label="ອັບເດດລ່າສຸດ"
          value={formatDate({
            date: soe?.updatedAt,
            format: "DD/MM/YYYY",
          })}
        />
        <InfoItem label="ອັບເດດໂດຍ" value={soe?.updatedBy} />
      </div>
    </div>
  );
}
